import { useEffect, type ReactElement, type ReactNode } from 'react';
import { X } from 'lucide-react';

export type PracticeBottomSheetProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  eyebrow?: string;
  status?: ReactNode;
  maxHeight?: string;
};

const DEFAULT_MAX_HEIGHT = '82%';

// The one slide-up drawer idiom for the practice surface (tablet portrait +
// mobile). Light surface to match the practice surface. Labelled header +
// close; grab handle for touch. Shared with #382's settings drawer so the app
// has a single drawer pattern.
export function PracticeBottomSheet({
  open,
  title,
  onClose,
  children,
  eyebrow,
  status,
  maxHeight = DEFAULT_MAX_HEIGHT,
}: PracticeBottomSheetProps): ReactElement | null {
  useEffect(() => {
    if (!open) return;
    const handleKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-[70] flex flex-col justify-end"
    >
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onClose}
        className="absolute inset-0 bg-[var(--ink)]/40"
      />
      <div
        className="relative flex flex-col overflow-hidden rounded-t-[var(--r-lg)] border-t border-[var(--rule-strong)] bg-[var(--bg-surface)] text-[var(--ink)] shadow-[var(--shadow-modal)]"
        style={{ maxHeight }}
      >
        <div className="flex shrink-0 justify-center pb-1 pt-2.5">
          <span aria-hidden="true" className="h-1 w-10 rounded-full bg-[var(--ink-4)]" />
        </div>
        <div className="flex shrink-0 items-center gap-3 border-b border-[var(--rule)] px-5 py-3">
          <div className="min-w-0">
            {eyebrow && (
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-3)]">
                {eyebrow}
              </div>
            )}
            <div className="font-display text-[19px] font-semibold leading-tight">{title}</div>
          </div>
          {status && <div className="ml-auto">{status}</div>}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={`${status ? '' : 'ml-auto'} rounded-full p-1.5 text-[var(--ink-3)] transition-colors hover:bg-[var(--bg-soft)] hover:text-[var(--ink)]`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="nice-scroll min-h-0 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
